import { meQueryOptions } from "@/api/auth";
import { MemberFeatures, useMemberFeatures } from "@/api/me";
import { connectSockets, queryClient } from "@/api/utils";
import { WhatsNewDialog } from "@/components/first-time-dialog/WhatsNew";
import { devicePreviewCommand } from "@/modules/command-palette/device-preview";
import { envCommand } from "@/modules/command-palette/env";
import { featureFlagCommands } from "@/modules/command-palette/feature-flags";
import { mockOnboardingCommand } from "@/modules/command-palette/mock-onboarding";
import { addCommands, CommandPalette } from "@givenwell/command-palette";
import { Box, Flex } from "@givenwell/components";
import {
  ChatBubbleOvalSmileyIcon,
  ChatBubbleTextSquareIcon,
  CoinShareIcon,
  FastfowardClockIcon,
  OpenBookIcon,
  ReceiptIcon,
  StoreIcon,
} from "@givenwell/core-solid-icons";
import { colors } from "@givenwell/theme";
import {
  createFileRoute,
  getRouteApi,
  Link,
  Outlet,
  RegisteredRouter,
  RouteIds,
  useMatches,
} from "@tanstack/react-router";
import { ReactNode, useEffect, useMemo, useRef } from "react";
import { boolean, fallback, number, object, optional, string } from "valibot";
import { BottomNav, BottomNavLink } from "./BottomNav";
import { DesktopSidebar } from "./DesktopSidebar";

export const Route = createFileRoute("/_auth/_app")({
  validateSearch: object({
    "first-time": fallback(optional(boolean()), undefined),
    fullscreen: fallback(optional(boolean()), undefined),
    "hide-mp": fallback(optional(boolean()), undefined),
    review: optional(
      object({
        purchaseId: string(),
        stars: optional(number()),
      }),
    ),
  }),
  loader: () => {
    queryClient.prefetchQuery(meQueryOptions);
  },
  pendingComponent: PendingAppLayout,
  component: AppLayout,
  notFoundComponent: NotFoundComponent,
});

// ------------------------------------------------------------

type RouteId = RouteIds<RegisteredRouter["routeTree"]>;

const routesWithMobileNav: RouteId[] = [
  "/_auth/_app/marketplace/",
  "/_auth/_app/messages/",
  "/_auth/_app/purchases/",
  "/_auth/_app/resources/",
  "/_auth/_app/favourites",
  "/_auth/_app/claims/",
  "/_auth/_app/eap/",
  "/_auth/_app/settings",
  "/_auth/_app/home",
];

// evil
const routesWithMobileNavOnlyOnSmBreakpoint: RouteId[] = ["/_auth/_app/messages/$conversationId"];

const routesWithPersistentDesktopSidebar: RouteId[] = [
  "/_auth/_app/marketplace/",
  "/_auth/_app/messages",
  "/_auth/_app/purchases/",
  "/_auth/_app/resources/",
  "/_auth/_app/favourites",
  "/_auth/_app/claims",
  "/_auth/_app/claims/",
  "/_auth/_app/claims/help",
  "/_auth/_app/wallet",
  "/_auth/_app/marketplace/suppliers/$supplierId",
  "/_auth/_app/eap",
];

const route = getRouteApi("/_auth/_app");

function AppLayout() {
  const sidebarIsPersistent = useMatches({
    select(matches) {
      return matches.some(match => routesWithPersistentDesktopSidebar.includes(match.routeId));
    },
  });

  const scrollContainerRef = useRef<HTMLDivElement>(null!);

  useEffect(() => {
    connectSockets();
  }, []);

  const { fullscreen } = route.useSearch();

  useEffect(() => {
    if (import.meta.env.DEV) {
      return addCommands([devicePreviewCommand, mockOnboardingCommand, envCommand, ...featureFlagCommands]);
    }
  }, []);

  return (
    <Flex css={{ height: "100%", overflow: "hidden" }}>
      <Box css={{ display: "none", "@lg": { display: "block", width: 200 } }}>
        <DesktopSidebar />
      </Box>
      <Flex css={{ flexDirection: "column", flex: "1 0 0px", overflow: "hidden" }}>
        {/* Mobile "Top Bar" */}
        <Box
          data-fullscreen={fullscreen ? "" : undefined}
          id="status-bar"
          css={{
            minHeight: "var(--safe-area-inset-top)",
            zIndex: 1000,
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            // backgroundColor: "transparent",
            backgroundColor: colors.cream + "60",
            backdropFilter: "blur(4px)",
            "&[data-fullscreen]": {},
          }}
        />
        <Flex
          ref={scrollContainerRef}
          css={{
            flexDir: "column",
            flex: "1 0 0px",
            position: "relative",
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          <Outlet />
        </Flex>
      </Flex>

      <BottomNavWithItems />
      <WhatsNewDialog />
      <CommandPalette />
    </Flex>
  );
}

function BottomNavWithItems() {
  const shouldShowMobileNav = useMatches({
    select(matches) {
      return matches.some(match => routesWithMobileNav.includes(match.routeId));
    },
  });
  const shouldShowMobileNavOnSmBreakpoint = useMatches({
    select(matches) {
      return matches.some(match => routesWithMobileNavOnlyOnSmBreakpoint.includes(match.routeId));
    },
  });

  const caps = useMemberFeatures();
  const items = useMemo(() => getNavItems(caps), [caps]);

  return (
    <BottomNav
      css={{
        display: shouldShowMobileNav ? "flex" : "none",
        "@sm": {
          display: shouldShowMobileNavOnSmBreakpoint ? "flex" : undefined,
        },
      }}
    >
      {items}
    </BottomNav>
  );
}

function getNavItems(features: MemberFeatures) {
  const { hasAllowance, hasAllowanceMarketplace, hasEap, hasEapMarketplace, hasSupporters } = features;

  const showCombinedClaimsPage = !hasAllowanceMarketplace && !hasEapMarketplace && hasAllowance && hasEap;
  const hasAnyMarketplace = hasAllowanceMarketplace || hasEapMarketplace;

  const rawItems: ReactNode[] = [
    showCombinedClaimsPage && (
      <BottomNavLink
        to="/home"
        icon={<CoinShareIcon />}
        activeIcon={<CoinShareIcon />}
        label="My Benefits"
      ></BottomNavLink>
    ),
    hasAllowanceMarketplace && (
      <BottomNavLink
        to="/marketplace"
        icon={<StoreIcon />}
        activeIcon={<StoreIcon />}
        label="Marketplace"
      ></BottomNavLink>
    ),
    !hasAllowanceMarketplace && hasAllowance && !showCombinedClaimsPage && (
      <BottomNavLink to="/claims" icon={<StoreIcon />} label="Allowance"></BottomNavLink>
    ),
    hasEapMarketplace && (
      <BottomNavLink to="/eap" icon={<ChatBubbleOvalSmileyIcon />} label="Free Sessions"></BottomNavLink>
    ),
    !hasEapMarketplace && hasEap && !showCombinedClaimsPage && (
      <BottomNavLink to="/claims" icon={<ChatBubbleOvalSmileyIcon />} label="Free Sessions"></BottomNavLink>
    ),
    (hasAnyMarketplace || (hasEap && hasAllowance)) && (
      <BottomNavLink to="/claims" icon={<ReceiptIcon />} label="My Claims"></BottomNavLink>
    ),
    hasSupporters && <BottomNavLink to="/resources" icon={<OpenBookIcon />} label="Wellness Hub"></BottomNavLink>,
  ];

  const items = rawItems.filter(Boolean);

  return items;
}

function PendingAppLayout() {
  const shouldShowMobileNav = useMatches({
    select(matches) {
      return matches.some(match => routesWithMobileNav.includes(match.routeId));
    },
  });
  const shouldShowMobileNavOnSmBreakpoint = useMatches({
    select(matches) {
      return matches.some(match => routesWithMobileNavOnlyOnSmBreakpoint.includes(match.routeId));
    },
  });

  const scrollContainerRef = useRef<HTMLDivElement>(null!);

  const { fullscreen } = route.useSearch();

  return (
    <Flex css={{ flexDirection: "column", height: "100%" }}>
      {/* Mobile "Top Bar" */}
      <Box
        data-fullscreen={fullscreen ? "" : undefined}
        css={{
          minHeight: "var(--safe-area-inset-top)",
          backgroundColor: "white",
          zIndex: 1000,
          "@lg": {
            display: "none",
          },
          "&[data-fullscreen]": {
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            backgroundColor: "transparent",
            backdropFilter: "blur(10px)",
          },
        }}
      />
      {/* Desktop Top bar */}
      <Box css={{ height: "calc(64px + var(--safe-area-inset-top))" }} />
      <Flex
        ref={scrollContainerRef}
        css={{
          flexDir: "column",
          flex: "1 0 0px",
          position: "relative",
          overflowY: "auto",
        }}
      >
        <Outlet />
      </Flex>

      <BottomNav
        css={{
          display: shouldShowMobileNav ? "flex" : "none",
          "@sm": {
            display: shouldShowMobileNavOnSmBreakpoint ? "flex" : undefined,
          },
        }}
      >
        <BottomNavLink label="Marketplace" icon={<StoreIcon />} activeIcon={<StoreIcon />} to="/marketplace" />
        <BottomNavLink
          label="Messages"
          icon={<ChatBubbleTextSquareIcon />}
          activeIcon={<ChatBubbleTextSquareIcon />}
          to="/messages"
        />
        <BottomNavLink label="Purchases" icon={<FastfowardClockIcon />} to="/purchases" />
      </BottomNav>
    </Flex>
  );
}

function NotFoundComponent() {
  return (
    <div>
      <h2>Nothing to see here!</h2>
      <p>
        <Link to="/">Go to the home page</Link>
      </p>
    </div>
  );
}
